import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { AccountService } from '../../services/account.service';
import { CustomerService } from '../../services/customer.service';
import { SegmentService } from '../../services/segment.service';
import { RemittanceService } from '../../services/collections.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
  DeductionInput, DeductionKind, InvoiceSuggestion, Remittance, RemittanceCreate, RemittancePreview,
} from '../../interfaces/sales/collections.interface';
import { ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import SearchableSelect from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { bankDepositOptions, depositAccountOptions, leafAccounts } from '../../utils/postingAccounts';
import { BankAccountService } from '../../services/bankAccount.service';

const METHODS: ReceiptMethod[] = ['CASH', 'BANK_TRANSFER', 'CHEQUE', 'MOBILE_BANKING', 'OTHER'];
const KINDS: DeductionKind[] = ['TDS', 'VDS', 'BANK_CHARGE', 'OTHER'];

/** W3-N2: customer remittances (a payment net of TDS / VDS / bank charge), list + create + reverse. */
const CustomerRemittances: React.FC = () => {
  const institutionId = AuthService.getSelectedInstitution() as string;
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canManage = hasPermission(PERMISSIONS.RECEIPT_MANAGE);
  const [rows, setRows] = useState<Remittance[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<Remittance | null>(null);
  const [confirmReverse, setConfirmReverse] = useState<Remittance | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await RemittanceService.list(institutionId, undefined, page, 20);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load remittances');
    } finally {
      setLoading(false);
    }
  }, [institutionId, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const reverse = async (r: Remittance) => {
    try {
      await RemittanceService.reverse(r.id);
      toast.showSuccess(`${r.remittanceNumber} reversed`);
      setDetail(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse the remittance');
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.remittances.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Customer Remittances</Typography>
        {canManage && <Button variant="contained" startIcon={<AddIcon />} data-help="sales.remittances.new" onClick={() => setCreating(true)}>New remittance</Button>}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.remittances.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.remittances.col.number">Number</TableCell><TableCell data-help="sales.remittances.col.date">Date</TableCell><TableCell data-help="sales.remittances.col.customer">Customer</TableCell>
                <TableCell align="right" data-help="sales.remittances.col.cash">Cash</TableCell><TableCell align="right" data-help="sales.remittances.col.deductions">Deductions</TableCell>
                <TableCell align="right" data-help="sales.remittances.col.gross">Gross</TableCell><TableCell data-help="sales.remittances.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={7} align="center">No remittances yet.</TableCell></TableRow>}
              {rows.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} data-testid={`remittance-row-${r.id}`}
                  onClick={async () => setDetail(await RemittanceService.get(r.id))}>
                  <TableCell>{r.remittanceNumber}</TableCell>
                  <TableCell>{formatDate(r.remittanceDate)}</TableCell>
                  <TableCell>{r.customerName}</TableCell>
                  <TableCell align="right">{formatMoney(r.cashAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(r.deductionsAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(r.grossAmount)}</TableCell>
                  <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={r.status === 'POSTED' ? 'success' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination component="div" count={total} page={page} rowsPerPage={20} rowsPerPageOptions={[20]}
            onPageChange={(_, p) => setPage(p)} />
        </TableContainer>
      )}
      {confirmReverse && (
        <Dialog open onClose={() => setConfirmReverse(null)} data-help-page="sales.remittances.reverse.page">
          <DialogTitle>Reverse {confirmReverse.remittanceNumber}?</DialogTitle>
          <DialogContent data-help-section="sales.remittances.reverse.section.main">
            <Typography variant="body2">
              This unapplies every invoice it settled and posts a mirror journal. It cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button data-help="sales.remittances.reverse.cancel" onClick={() => setConfirmReverse(null)}>Cancel</Button>
            <Button color="error" variant="contained" data-help="sales.remittances.reverse.confirm" onClick={() => { const r = confirmReverse; setConfirmReverse(null); reverse(r); }}>Reverse</Button>
          </DialogActions>
        </Dialog>
      )}
      {creating && <CreateDialog institutionId={institutionId} onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); load(); }} />}
      {detail && (
        <Dialog open onClose={() => setDetail(null)} maxWidth="sm" fullWidth data-help-page="sales.remittances.detail.page">
          <DialogTitle>{detail.remittanceNumber} - {detail.customerName}</DialogTitle>
          <DialogContent data-help-section="sales.remittances.detail.section.main">
            <Typography variant="body2">Cash {formatMoney(detail.cashAmount)} + deductions {formatMoney(detail.deductionsAmount)} = gross {formatMoney(detail.grossAmount)}</Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>Applied {formatMoney(detail.allocatedAmount)}, unapplied {formatMoney(detail.unappliedAmount)}</Typography>
            {detail.deductions.map(d => (
              <Typography key={d.id} variant="body2">{humanizeEnum(d.kind)}: {formatMoney(d.amount)}{d.certificateNumber ? ` (cert ${d.certificateNumber})` : ''}</Typography>
            ))}
            {detail.allocations.map(a => (
              <Typography key={a.allocationId} variant="body2">{a.invoiceNumber}: {formatMoney(a.amount)} ({humanizeEnum(a.status)})</Typography>
            ))}
          </DialogContent>
          <DialogActions>
            {canManage && detail.status === 'POSTED' && <Button color="error" data-help="sales.remittances.detail.reverse" onClick={() => setConfirmReverse(detail)}>Reverse</Button>}
            <Button data-help="sales.remittances.detail.close" onClick={() => setDetail(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const CreateDialog: React.FC<{ institutionId: string; onClose: () => void; onCreated: () => void }> = ({ institutionId, onClose, onCreated }) => {
  const toast = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [bankGlIds, setBankGlIds] = useState<string[] | null>(null);   // polish 4: null = unknown (no access / not loaded)
  const [segments, setSegments] = useState<Segment[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [depositId, setDepositId] = useState('');
  const [cash, setCash] = useState('');
  const [method, setMethod] = useState<ReceiptMethod>('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [deductions, setDeductions] = useState<DeductionInput[]>([]);
  const [invoices, setInvoices] = useState<InvoiceSuggestion[]>([]);
  const [applied, setApplied] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<RemittancePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [idemKey] = useState(() => `rem-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    (async () => {
      const all: Customer[] = [];
      for (let pg = 0; pg < 50; pg++) {   // page through every customer; no 200 cap
        const r = await CustomerService.listByInstitution(institutionId, pg, 200, 'ALL');
        all.push(...r.content);
        if (pg + 1 >= (r.totalPages ?? 1)) break;
      }
      setCustomers(all);
    })().catch(e => toast.showApiError(e, 'Failed to load customers'));
    SegmentService.getByInstitution(0, 200).then(r => setSegments(Array.isArray(r) ? r : r.content ?? []))
      .catch(e => toast.showApiError(e, 'Failed to load segments'));
    AccountService.getAllAccounts().then(setAccounts).catch(e => toast.showApiError(e, 'Failed to load accounts'));
    Promise.resolve(BankAccountService.listByInstitution(institutionId))
      .then(rows => setBankGlIds((rows ?? []).map(b => b.glCashAccountId)))
      .catch(() => setBankGlIds(null));   // no access to bank accounts: keep the plain cash-account list
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!customerId) { setInvoices([]); return; }
    RemittanceService.suggest(institutionId, customerId).then(setInvoices)
      .catch(e => toast.showApiError(e, 'Failed to load open invoices'));
  }, [customerId, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const assets = useMemo(
    () => bankDepositOptions(depositAccountOptions(accounts.filter(a => a.accountType === 'ASSET')), bankGlIds, method),
    [accounts, bankGlIds, method]);
  const postingAccounts = useMemo(() => leafAccounts(accounts), [accounts]);
  const deductionTotal = deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0);
  const gross = (Number(cash) || 0) + deductionTotal;

  const buildRequest = (): RemittanceCreate => ({
    customerId, remittanceDate: date, depositAccountId: depositId, cashAmount: Number(cash), method,
    reference: reference.trim() || undefined, idempotencyKey: idemKey,
    deductions: deductions.map(d => ({ ...d, amount: Number(d.amount) })),
    allocations: Object.entries(applied).filter(([, v]) => Number(v) > 0)
      .map(([salesInvoiceId, v]) => ({ salesInvoiceId, amount: Number(v) })),
  });
  const deductionsOk = deductions.every(d => Number(d.amount) > 0
    && (d.kind !== 'OTHER' || !!d.accountId) && (d.kind !== 'BANK_CHARGE' || !!d.segmentId));
  const valid = !!customerId && !!depositId && Number(cash) > 0 && deductionsOk;

  const doPreview = async () => {
    try { setPreview(await RemittanceService.preview(buildRequest())); } catch (e) { toast.showApiError(e, 'Preview failed'); }
  };
  const submit = async () => {
    try {
      setBusy(true);
      const r = await RemittanceService.create(buildRequest());
      toast.showSuccess(`Remittance ${r.remittanceNumber} recorded`);
      onCreated();
    } catch (e) {
      toast.showApiError(e, 'Failed to record the remittance');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth data-help-page="sales.remittances.create.page">
      <DialogTitle>New customer remittance</DialogTitle>
      <DialogContent data-help-section="sales.remittances.create.section.form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <SearchableSelect<Customer> data-help="sales.remittances.create.customer" label="Customer" options={customers} value={customerId} required
          getOptionLabel={c => c.name} onChange={id => setCustomerId(id)} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="sales.remittances.create.date" label="Date" type="date" value={date} InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 160px', maxWidth: 220 }} onChange={e => setDate(e.target.value)} />
          <TextField data-help="sales.remittances.create.cash" label="Cash received" type="number" value={cash} sx={{ flex: '1 1 160px', maxWidth: 220 }}
            onChange={e => setCash(e.target.value)} />
          <TextField select data-help="sales.remittances.create.method" label="Method" value={method} sx={{ flex: '1 1 160px', maxWidth: 220 }}
            onChange={e => setMethod(e.target.value as ReceiptMethod)}>
            {METHODS.map(m => <MenuItem key={m} value={m}>{humanizeEnum(m)}</MenuItem>)}
          </TextField>
          <TextField data-help="sales.remittances.create.reference" label="Reference" value={reference} sx={{ flex: '1 1 160px', maxWidth: 260 }}
            onChange={e => setReference(e.target.value)} />
        </Box>
        <AccountAutocomplete data-help="sales.remittances.create.depositAccount" accounts={assets} value={depositId} label="Deposit account" required onChange={setDepositId} />

        <Typography variant="subtitle2">Deductions (TDS, VDS, bank charges)</Typography>
        {deductions.map((d, i) => (
          <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <TextField select size="small" data-help="sales.remittances.create.deduction.kind" label="Kind" value={d.kind} sx={{ flex: '1 1 130px', maxWidth: 170 }}
              onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, kind: e.target.value as DeductionKind } : x))}>
              {KINDS.map(k => <MenuItem key={k} value={k}>{humanizeEnum(k)}</MenuItem>)}
            </TextField>
            <TextField size="small" data-help="sales.remittances.create.deduction.amount" label="Amount" type="number" value={d.amount || ''} sx={{ flex: '1 1 130px', maxWidth: 170 }}
              onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x))} />
            <TextField size="small" data-help="sales.remittances.create.deduction.certNo" label="Certificate no." value={d.certificateNumber ?? ''} sx={{ flex: '1 1 150px', maxWidth: 220 }}
              onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, certificateNumber: e.target.value } : x))} />
            <TextField size="small" data-help="sales.remittances.create.deduction.certDate" label="Certificate date" type="date" value={d.certificateDate ?? ''} InputLabelProps={{ shrink: true }}
              sx={{ flex: '1 1 150px', maxWidth: 200 }}
              onChange={e => setDeductions(deductions.map((x, j) => j === i ? { ...x, certificateDate: e.target.value || undefined } : x))} />
            {d.kind === 'OTHER' && (
              <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                <AccountAutocomplete data-help="sales.remittances.create.deduction.account" accounts={postingAccounts} value={d.accountId ?? ''} label="Account" required size="small"
                  onChange={id => setDeductions(deductions.map((x, j) => j === i ? { ...x, accountId: id || undefined } : x))} />
              </Box>
            )}
            {d.kind === 'BANK_CHARGE' && (
              <Box sx={{ flex: '1 1 200px', maxWidth: 280 }}>
                <SearchableSelect<Segment> data-help="sales.remittances.create.deduction.segment" label="Segment" required options={segments} value={d.segmentId ?? ''}
                  getOptionLabel={g => g.name} onChange={id => setDeductions(deductions.map((x, j) => j === i ? { ...x, segmentId: id || undefined } : x))} />
              </Box>
            )}
            <IconButton aria-label="Remove deduction" data-help="sales.remittances.create.deduction.remove" onClick={() => setDeductions(deductions.filter((_, j) => j !== i))}><DeleteIcon /></IconButton>
          </Box>
        ))}
        <Box><Button size="small" data-help="sales.remittances.create.deduction.add" onClick={() => setDeductions([...deductions, { kind: 'TDS', amount: 0 }])}>Add deduction</Button></Box>

        <Typography variant="subtitle2">Apply to invoices (gross {formatMoney(gross)})</Typography>
        {invoices.length === 0 && <Typography variant="body2">{customerId ? 'No open invoices.' : 'Pick a customer.'}</Typography>}
        {invoices.map(i => (
          <Box key={i.salesInvoiceId} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flex: '1 1 220px' }}>{i.invoiceNumber} - outstanding {formatMoney(i.outstanding)}</Typography>
            <TextField size="small" type="number" data-help="sales.remittances.create.apply" label="Apply" value={applied[i.salesInvoiceId] ?? ''}
              sx={{ flex: '1 1 130px', maxWidth: 170 }}
              onChange={e => setApplied({ ...applied, [i.salesInvoiceId]: e.target.value })} />
          </Box>
        ))}
        {preview && (
          <Box data-testid="remittance-preview">
            <Alert severity={preview.warnings.length ? 'warning' : 'info'} sx={{ mb: 1 }}>
              Debits {formatMoney(preview.totalDebit)} = credits {formatMoney(preview.totalCredit)}; applied {formatMoney(preview.allocatedAmount)},
              unapplied {formatMoney(preview.unappliedAmount)}. {preview.warnings.join(' ')}
            </Alert>
            <TableContainer component={Paper} variant="outlined" data-help-section="sales.remittances.create.preview">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.remittances.create.preview.col.account">Account</TableCell>
                    <TableCell data-help="sales.remittances.create.preview.col.segment">Segment</TableCell>
                    <TableCell align="right" data-help="sales.remittances.create.preview.col.debit">Debit</TableCell>
                    <TableCell align="right" data-help="sales.remittances.create.preview.col.credit">Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.lines.map((l, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{l.accountName ?? humanizeEnum(l.role)}</TableCell>
                      <TableCell>{segments.find(sg => sg.id === l.segmentId)?.name ?? '-'}</TableCell>
                      <TableCell align="right">{l.entryType === 'DEBIT' ? formatMoney(l.amount) : ''}</TableCell>
                      <TableCell align="right">{l.entryType === 'CREDIT' ? formatMoney(l.amount) : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="sales.remittances.create.cancel" onClick={onClose}>Cancel</Button>
        <Button disabled={!valid} data-help="sales.remittances.create.preview.run" onClick={doPreview}>Preview journal</Button>
        <Button variant="contained" disabled={!valid || busy} data-help="sales.remittances.create.save" onClick={submit}>Record remittance</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerRemittances;
